import React from 'react';
import AppConfig from 'src/config/config';
import http from 'src/utils/api/http';
import * as T from './Link.types';
import * as S from './Link.styles';

const { REACT_APP_API_BASE_URL } = AppConfig;

const isApiUrl = (href?: string) =>
  Boolean(href) &&
  (href.startsWith(REACT_APP_API_BASE_URL) || href.includes('/subscriptions/documents/'));

// API resources require the X-Tenant-Id header, which a plain <a> navigation cannot send.
// Fetch through the shared axios instance (which carries the header) and open the blob instead.
const handleApiDownload = async (event: React.MouseEvent, href: string) => {
  event.preventDefault();
  try {
    const response = await http.get(href, { responseType: 'blob' });
    const objectUrl = URL.createObjectURL(
      new Blob([response.data], { type: response.headers['content-type'] }),
    );
    window.open(objectUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
  } catch (error) {
    console.error('Something went wrong in handleApiDownload() - Link', error);
  }
};

const LinkContent = ({ isReverted, chevron, linkText }: T.LinkContentProps) => (
  <S.LinkContent isReverted={isReverted}>
    {chevron !== 'none' && (
      <S.ChevronContainer chevron={chevron} isReverted={isReverted}>
        <S.ChevronLink size="m" chevron={chevron} />
      </S.ChevronContainer>
    )}
    <span>{linkText}</span>
  </S.LinkContent>
);

const Link = ({
  dataTestId,
  linkText,
  to,
  chevron = 'right',
  href,
  variants = 'primary',
  isReverted = false,
  hasComma = false,
  isUnderlined = true,
  onClick,
  ...rest
}: T.LinkProps) =>
  to ? (
    <S.RouterLink
      chevron={chevron}
      isReverted={isReverted}
      to={to}
      data-testid={dataTestId}
      variants={variants}
      isUnderlined={isUnderlined}
      onClick={onClick}
      {...rest}
    >
      <LinkContent isReverted={isReverted} chevron={chevron} linkText={linkText} />
    </S.RouterLink>
  ) : (
    <>
      <S.ClassicLink
        chevron={chevron}
        href={href}
        data-testid={dataTestId}
        variants={variants}
        isReverted={isReverted}
        isUnderlined={isUnderlined}
        onClick={isApiUrl(href) ? (event) => handleApiDownload(event, href) : onClick}
        {...rest}
      >
        <LinkContent isReverted={isReverted} chevron={chevron} linkText={linkText} />
      </S.ClassicLink>
      {hasComma && ','}
    </>
  );

export default Link;
